import { createFileRoute } from '@tanstack/react-router';
import { Card, Group, Select, SegmentedControl, Stack, Text, Title, useMantineColorScheme } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeLanguage } from '@/shared/i18n/config';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { fetchList, createOne, updateOne } from '@/shared/api/jsonapi/client';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { notifications } from '@mantine/notifications';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/settings/preferences')({ component: PreferencesPage });

const LANGUAGES = [
  { value: 'it-IT', label: 'Italiano' },
  { value: 'en-US', label: 'English' },
];

const TIMEZONES = [
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'UTC', label: 'UTC' },
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'gg/mm/aaaa (Italia)' },
  { value: 'MM/DD/YYYY', label: 'mm/gg/aaaa (USA)' },
  { value: 'YYYY-MM-DD', label: 'aaaa-mm-gg (ISO)' },
];

const LS_TZ = 'maefwk10:tz';
const LS_DF = 'maefwk10:date-format';
/** Chiave usata dal colorSchemeManager di default di Mantine (nessun manager custom in AppProviders). */
const LS_COLOR_SCHEME = 'mantine-color-scheme-value';

/** prefKey della UserPreference BE (store chiave/valore, stesso pattern di useFavoriteProjects). */
const PREF_KEY = 'ui-preferences';

interface UserPreferenceRow extends Denormalized {
  type: 'user-preferences';
  prefKey?: string;
  prefValue?: string;
}

/** Snapshot serializzato in prefValue (JSON). */
interface UiPrefs {
  colorScheme?: string;
  lang?: string;
  tz?: string;
  dateFormat?: string;
}

function PreferencesPage() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const [lang, setLang] = useState<string>(localStorage.getItem(STORAGE_KEYS.lang) ?? 'it-IT');
  const [tz, setTz] = useState<string>(localStorage.getItem(LS_TZ) ?? 'Europe/Rome');
  const [df, setDf] = useState<string>(localStorage.getItem(LS_DF) ?? 'DD/MM/YYYY');

  const qc = useQueryClient();
  const userId = useCurrentUserId();

  // Record BE (user-scoped lato server: si filtra solo per prefKey, come useFavoriteProjects).
  const prefQ = useQuery({
    queryKey: ['user-pref', PREF_KEY, userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const resp = await fetchList<UserPreferenceRow>('user-preferences', {
        filter: { prefKey: { eq: PREF_KEY } },
        page: { size: 1 },
      });
      return resp.data[0] ?? null;
    },
  });

  // Al mount: se esiste il record BE e il valore locale corrispondente è assente,
  // applichiamo il valore BE (localStorage resta autoritativo quando presente).
  const appliedRemote = useRef(false);
  useEffect(() => {
    if (appliedRemote.current || !prefQ.data?.prefValue) return;
    appliedRemote.current = true;
    let remote: UiPrefs;
    try {
      remote = JSON.parse(prefQ.data.prefValue) as UiPrefs;
    } catch {
      return; // prefValue corrotto: ignoriamo, vince il locale
    }
    if (remote.colorScheme && !localStorage.getItem(LS_COLOR_SCHEME)) {
      setColorScheme(remote.colorScheme as 'light' | 'dark' | 'auto');
    }
    if (remote.lang && !localStorage.getItem(STORAGE_KEYS.lang)) {
      localStorage.setItem(STORAGE_KEYS.lang, remote.lang);
      setLang(remote.lang);
    }
    if (remote.tz && !localStorage.getItem(LS_TZ)) {
      localStorage.setItem(LS_TZ, remote.tz);
      setTz(remote.tz);
    }
    if (remote.dateFormat && !localStorage.getItem(LS_DF)) {
      localStorage.setItem(LS_DF, remote.dateFormat);
      setDf(remote.dateFormat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefQ.data]);

  useEffect(() => {
    void changeLanguage(lang);
  }, [lang]);

  // Upsert BE: update se il record esiste, create altrimenti. Il fallimento è
  // non bloccante (warning): localStorage resta la fonte autoritativa locale.
  const upsert = useMutation({
    mutationFn: async (next: UiPrefs) => {
      const json = JSON.stringify(next);
      if (prefQ.data?.id) {
        await updateOne('user-preferences', { id: prefQ.data.id, attributes: { prefValue: json } });
      } else {
        await createOne('user-preferences', { attributes: { prefKey: PREF_KEY, prefValue: json } });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-pref', PREF_KEY, userId] }),
    onError: () =>
      notifications.show({
        color: 'yellow',
        title: 'Sincronizzazione non riuscita',
        message: 'Preferenza salvata solo su questo dispositivo.',
      }),
  });

  /** Snapshot corrente + patch della preferenza appena cambiata. */
  const syncRemote = (patch: Partial<UiPrefs>) => {
    if (!userId) return;
    upsert.mutate({ colorScheme, lang, tz, dateFormat: df, ...patch });
  };

  const persist = (key: string, value: string) => {
    localStorage.setItem(key, value);
    notifications.show({ color: 'green', message: 'Preferenza salvata', autoClose: 1500 });
  };

  return (
    <Stack>
      <Title order={2}>Preferenze</Title>
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Group justify="space-between">
            <div>
              <Text fw={600}>Tema</Text>
              <Text size="sm" c="dimmed">Chiaro, scuro o segui sistema</Text>
            </div>
            <SegmentedControl
              value={colorScheme}
              onChange={(v) => {
                setColorScheme(v as 'light' | 'dark' | 'auto');
                syncRemote({ colorScheme: v });
              }}
              data={[
                { label: 'Chiaro', value: 'light' },
                { label: 'Scuro', value: 'dark' },
                { label: 'Auto', value: 'auto' },
              ]}
            />
          </Group>
        </Stack>
      </Card>
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Select
            label="Lingua"
            value={lang}
            onChange={(v) => {
              if (!v) return;
              setLang(v);
              persist(STORAGE_KEYS.lang, v);
              syncRemote({ lang: v });
            }}
            data={LANGUAGES}
          />
          <Select
            label="Fuso orario"
            value={tz}
            onChange={(v) => {
              if (!v) return;
              setTz(v);
              persist(LS_TZ, v);
              syncRemote({ tz: v });
            }}
            data={TIMEZONES}
          />
          <Select
            label="Formato data"
            value={df}
            onChange={(v) => {
              if (!v) return;
              setDf(v);
              persist(LS_DF, v);
              syncRemote({ dateFormat: v });
            }}
            data={DATE_FORMATS}
          />
        </Stack>
      </Card>
    </Stack>
  );
}
